import { create } from "zustand";

export type Lang = "ko" | "en" | "ja";
export const LANGS: { code: Lang; label: string }[] = [
  { code: "ko", label: "한국어" },
  { code: "en", label: "English" },
  { code: "ja", label: "日本語" },
];

type Dict = Record<string, Record<Lang, string>>;

const DICT: Dict = {
  "bands.nav": { ko: "내 밴드", en: "My bands", ja: "マイバンド" },
  "brand.tagline": { ko: "연주 분석 · 코드 악보 · 연습", en: "Analyze · Chords · Practice", ja: "解析 · コード · 練習" },
  "brand.home": { ko: "처음 화면으로", en: "Back to start", ja: "最初の画面へ" },

  // song input
  "input.title": { ko: "곡 입력", en: "Add a song", ja: "曲を追加" },
  "input.drop": { ko: "오디오·영상 파일을 끌어다 놓기", en: "Drag & drop an audio/video file", ja: "音声・動画ファイルをドラッグ＆ドロップ" },
  "input.formats": { ko: "MP3 · WAV · MP4 · MOV · 최대 10분 · 100MB — 클릭해서 선택", en: "MP3 · WAV · MP4 · MOV · up to 10 min · 100MB — click to choose", ja: "MP3 · WAV · MP4 · MOV · 最大10分 · 100MB — クリックで選択" },
  "input.or": { ko: "또는", en: "or", ja: "または" },
  "input.yturl": { ko: "YouTube URL 붙여넣기", en: "Paste a YouTube URL", ja: "YouTube URL を貼り付け" },
  "input.analyze": { ko: "분석", en: "Analyze", ja: "解析" },
  "input.ythint": { ko: "YouTube는 10분 이하 영상만 분석할 수 있어요", en: "YouTube videos must be 10 minutes or shorter", ja: "YouTubeは10分以下の動画のみ解析できます" },
  "input.analyzing": { ko: "분석 중… 키·코드·박자 추출", en: "Analyzing… extracting key, chords, beats", ja: "解析中… キー・コード・拍子を抽出" },
  "input.analyzing_yt": { ko: "분석 중… (YouTube)", en: "Analyzing… (YouTube)", ja: "解析中…（YouTube）" },
  "input.queued": { ko: "분석 대기 중…", en: "Queued…", ja: "解析待ち…" },
  "input.err": { ko: "오류가 발생했어요. 잠시 후 다시 시도해 주세요.", en: "Something went wrong. Please try again in a moment.", ja: "エラーが発生しました。しばらくしてからもう一度お試しください。" },
  "input.toobig": { ko: "파일이 너무 큽니다 (최대 100MB)", en: "File too large (max 100MB)", ja: "ファイルが大きすぎます（最大100MB）" },
  "input.offline": { ko: "오프라인 — 분석은 인터넷 연결이 필요합니다", en: "Offline — analysis needs an internet connection", ja: "オフライン — 解析にはインターネット接続が必要です" },

  // analysis
  "analysis.title": { ko: "분석 결과", en: "Analysis", ja: "解析結果" },
  "analysis.tempo": { ko: "템포", en: "Tempo", ja: "テンポ" },
  "analysis.key": { ko: "키", en: "Key", ja: "キー" },
  "song.bars": { ko: "{n}마디", en: "{n} bars", ja: "{n}小節" },
  "song.new": { ko: "다른 곡", en: "New song", ja: "別の曲" },
  "ctrl.metrooffset": { ko: "메트로놈 타이밍", en: "Metronome timing", ja: "メトロノームのタイミング" },
  "ctrl.metrooffset_hint": { ko: "클릭이 늦게 들리면 + 쪽으로 (블루투스 이어폰은 크게)", en: "Clicks sound late? Move toward + (more for Bluetooth)", ja: "クリックが遅れて聞こえたら＋側へ（Bluetoothは大きめに）" },
  "ctrl.beatlevel": { ko: "박 단위 (분석 박을 반/두 배로)", en: "Beat level (halve/double detected beats)", ja: "拍の単位（検出拍を半分/2倍）" },
  "metro.grid": { ko: "메트로놈 박 기준", en: "Metronome grid", ja: "メトロノームの拍" },
  "metro.grid_fixed": { ko: "고정 템포", en: "Fixed", ja: "固定テンポ" },
  "metro.blocks": { ko: "{n}구간", en: "{n} blocks", ja: "{n}区間" },
  "metro.grid_detected": { ko: "검출된 박", en: "Detected beats", ja: "検出した拍" },
  "acct.account": { ko: "계정", en: "Account", ja: "アカウント" },
  "acct.logout": { ko: "로그아웃", en: "Log out", ja: "ログアウト" },
  "acct.mylinks": { ko: "내 링크", en: "My links", ja: "マイリンク" },
  "acct.login_hint": { ko: "로그인하면 곡 링크를 저장해 어느 기기에서나 다시 열 수 있어요. PDF 악보는 이 기기에만 보관돼요.", en: "Sign in to save song links and open them on any device. PDF sheets stay on this device.", ja: "ログインすると曲のリンクを保存して、どの端末からでも開けます。PDF楽譜はこの端末にだけ保存されます。" },
  "links.save": { ko: "이 곡 링크 저장", en: "Save this song", ja: "この曲を保存" },
  "links.unsave": { ko: "저장 해제", en: "Remove from my links", ja: "保存を解除" },
  "links.saved": { ko: "내 링크에 저장했어요", en: "Saved to My links", ja: "マイリンクに保存しました" },
  "links.removed": { ko: "내 링크에서 뺐어요", en: "Removed from My links", ja: "マイリンクから外しました" },
  "links.fail": { ko: "저장에 실패했어요", en: "Couldn't save", ja: "保存できませんでした" },
  "links.title": { ko: "내 링크", en: "My links", ja: "マイリンク" },
  "links.count": { ko: "{n}곡", en: "{n} songs", ja: "{n}曲" },
  "links.empty": { ko: "저장한 곡이 없어요.", en: "No saved songs yet.", ja: "保存した曲はまだありません。" },
  "links.empty_sub": { ko: "YouTube 곡을 열고 위쪽 북마크를 누르면\n여기에 모여요.", en: "Open a YouTube song and tap the bookmark\nat the top to keep it here.", ja: "YouTubeの曲を開いて上のブックマークを押すと\nここに集まります。" },
  "links.delete": { ko: "삭제", en: "Delete", ja: "削除" },
  "links.delete_confirm": { ko: "이 곡을 내 링크에서 뺄까요?", en: "Remove this song from My links?", ja: "この曲をマイリンクから外しますか?" },
  "links.close": { ko: "닫기", en: "Close", ja: "閉じる" },
  "links.pdf_here": { ko: "PDF 악보", en: "PDF sheet", ja: "PDF楽譜" },
  "links.pdf_here_hint": { ko: "이 기기에 저장된 PDF 악보가 함께 열려요", en: "A PDF sheet saved on this device opens with it", ja: "この端末に保存されたPDF楽譜が一緒に開きます" },
  "metro.timing": { ko: "클릭 타이밍", en: "Click timing", ja: "クリックのタイミング" },
  "metro.earlier": { ko: "클릭 5ms 앞당기기 (늦게 들릴 때)", en: "Click 5 ms earlier (if it sounds late)", ja: "クリックを5ms早める（遅れて聞こえるとき）" },
  "metro.later": { ko: "클릭 5ms 늦추기 (빠르게 들릴 때)", en: "Click 5 ms later (if it sounds early)", ja: "クリックを5ms遅らせる（早く聞こえるとき）" },
  "metro.timing_reset": { ko: "0으로 되돌리기", en: "Reset to 0", ja: "0に戻す" },
  "metro.off": { ko: "메트로놈 꺼짐", en: "Metronome off", ja: "メトロノーム オフ" },
  "metro.overlay": { ko: "메트로놈: 곡과 함께", en: "Metronome: with song", ja: "メトロノーム: 曲と一緒に" },
  "metro.solo": { ko: "메트로놈만 재생", en: "Metronome only", ja: "メトロノームのみ" },
  "metro.tag_overlay": { ko: "함께", en: "Mix", ja: "併用" },
  "metro.tag_solo": { ko: "단독", en: "Solo", ja: "単独" },
  "song.next": { ko: "다음", en: "Next", ja: "次" },
  "input.close": { ko: "닫기", en: "Close", ja: "閉じる" },

  // score section
  "score.title": { ko: "코드 악보", en: "Sheet", ja: "譜面" },
  "score.empty": { ko: "파일을 업로드하거나 YouTube 링크를 분석하면 코드 악보가 여기에 표시됩니다.", en: "Upload a file or analyze a YouTube link to see the chord chart here.", ja: "ファイルをアップロードするかYouTubeリンクを解析すると、ここにコード譜が表示されます。" },
  "score.tab": { ko: "PDF 악보", en: "PDF sheet", ja: "PDF楽譜" },
  "pdf.upload": { ko: "PDF 악보 올리기", en: "Add a PDF sheet", ja: "PDF楽譜を追加" },
  "pdf.upload_sub": { ko: "타브·오선 악보 PDF · 이 기기에만 저장되고 서버로 보내지 않아요", en: "TAB or notation PDF · stays on this device, never uploaded", ja: "TAB・五線譜PDF · この端末にのみ保存され、サーバーには送信しません" },
  "pdf.reading": { ko: "악보를 읽고 마디를 찾는 중…", en: "Reading the sheet and finding measures…", ja: "楽譜を読み込み小節を検出中…" },
  "pdf.loading": { ko: "악보를 불러오는 중…", en: "Loading sheet…", ja: "楽譜を読み込み中…" },
  "pdf.nosong": { ko: "곡을 먼저 분석하면 PDF 악보를 연결할 수 있어요.", en: "Analyze a song first to attach a PDF sheet.", ja: "先に曲を解析するとPDF楽譜を紐付けられます。" },
  "pdf.counts": { ko: "악보 {s}마디 · 음원 {a}마디", en: "Sheet {s} bars · audio {a} bars", ja: "楽譜{s}小節 · 音源{a}小節" },
  "pdf.offset": { ko: "코드로 맞춤: 음원 {n}마디부터", en: "Matched by chords: from audio bar {n}", ja: "コードで同期: 音源{n}小節から" },
  "pdf.replace": { ko: "PDF 바꾸기", en: "Replace", ja: "差し替え" },
  "pdf.delete": { ko: "PDF 삭제", en: "Remove PDF", ja: "PDFを削除" },
  "pdf.delete_confirm": { ko: "한 번 더 누르면 삭제", en: "Tap again to remove", ja: "もう一度押すと削除" },
  "pdf.nomeasures": { ko: "마디를 찾지 못했어요. Guitar Pro·MuseScore 등에서 내보낸 디지털 PDF에서 가장 잘 동작합니다 (스캔본은 인식이 어려워요).", en: "No measures found. Works best with digital PDFs exported from Guitar Pro, MuseScore, etc. (scans are hard to read).", ja: "小節が見つかりません。Guitar Pro・MuseScoreなどから書き出したデジタルPDFで最もよく動作します（スキャンは認識が困難）。" },
  "pdf.err": { ko: "PDF를 열지 못했어요. 다른 파일로 시도해 주세요.", en: "Couldn't open this PDF. Try another file.", ja: "PDFを開けませんでした。別のファイルでお試しください。" },
  "pdf.notpdf": { ko: "PDF 파일만 올릴 수 있어요.", en: "Only PDF files are supported.", ja: "PDFファイルのみ対応しています。" },
  "pdf.toobig": { ko: "PDF가 너무 커요 (최대 {n}MB)", en: "PDF too large (max {n}MB)", ja: "PDFが大きすぎます（最大{n}MB）" },
  "pdf.page": { ko: "악보 {n}쪽", en: "Sheet page {n}", ja: "楽譜{n}ページ" },
  "pdf.measure": { ko: "악보 {n}마디", en: "Sheet bar {n}", ja: "楽譜{n}小節" },
  "score.chord": { ko: "코드", en: "Chords", ja: "コード" },

  // chord grid
  "grid.shapes": { ko: "코드 모양 (예시 운지)", en: "Chord shapes (example fingerings)", ja: "コードの形（運指例）" },
  "grid.empty": { ko: "분석된 코드가 없습니다.", en: "No chords analyzed.", ja: "解析されたコードがありません。" },

  // play styles (chips above the shape strip)
  "style.label": { ko: "연주 스타일", en: "Playing style", ja: "演奏スタイル" },
  "style.orig": { ko: "원래대로", en: "As analyzed", ja: "解析どおり" },
  "style.drone": { ko: "개방현 울림", en: "Ringing strings", ja: "開放弦の響き" },
  "style.walk": { ko: "베이스 잇기", en: "Walking bass", ja: "ベースをつなぐ" },
  "style.power": { ko: "쉽게", en: "Easy", ja: "かんたん" },
  "style.triad": { ko: "고음 3줄", en: "Top 3 strings", ja: "高音3弦" },
  "style.rec": { ko: "추천", en: "Suggested", ja: "おすすめ" },
  "style.desc_drone_open": { ko: "1·2번 줄을 개방현(B·E)으로 계속 울리며 코드에 색을 더해요.", en: "The open B and E strings keep ringing and add colour to each chord.", ja: "1・2弦の開放弦（B・E）を鳴らし続けて、コードに色を加えます。" },
  "style.desc_drone_fret3": { ko: "1·2번 줄 3프렛(G·D)을 누른 채 나머지 손가락만 바꿔 잡아요.", en: "Keep the 3rd fret held on the top two strings (G and D) and move only the other fingers.", ja: "1・2弦の3フレット（G・D）を押さえたまま、ほかの指だけ動かします。" },
  "style.desc_walk": { ko: "베이스가 한 음씩 이어지도록 일부 코드를 분수 코드로 바꿔요.", en: "Some chords become slash chords so the bass moves one step at a time.", ja: "ベースが一音ずつつながるよう、一部のコードを分数コードにします。" },
  "style.desc_power": { ko: "근음과 5도만 잡는 파워코드예요. 바레 코드 {n}개가 사라지지만 장·단조 구분도 없어져요.", en: "Power chords: root and fifth only. {n} barre chords go away, and so does major vs. minor.", ja: "ルートと5度だけのパワーコードです。セーハ{n}個がなくなりますが、長調・短調の区別もなくなります。" },
  "style.desc_triad": { ko: "1~3번 줄만 써서 앞 코드와 가까운 자리로 이어 잡아요. 합주할 때 다른 악기와 덜 겹쳐요.", en: "Triads on the top three strings, each placed close to the one before. Leaves room for the band.", ja: "1〜3弦だけで、前のコードに近い位置でつなぎます。合奏でほかの楽器と重なりにくくなります。" },
  "style.note": { ko: "녹음 분석이 아닌 연주 제안이에요.", en: "A playing suggestion, not part of the analysis.", ja: "録音の解析ではなく、演奏の提案です。" },
  "style.from": { ko: "{c}에서", en: "from {c}", ja: "{c}から" },
  "style.capo_applied": { ko: "카포를 {n}프렛에 맞췄어요. 원곡 소리는 그대로예요.", en: "Capo moved to fret {n}. The recording keeps its pitch.", ja: "カポを{n}フレットにしました。原曲の音はそのままです。" },
  "style.undo": { ko: "되돌리기", en: "Undo", ja: "元に戻す" },
  "style.meta": { ko: "연주 제안: {s}", en: "Playing suggestion: {s}", ja: "演奏の提案: {s}" },

  // key · capo (chords are redrawn; the recording keeps its pitch)
  "key.open": { ko: "키와 카포 바꾸기, 지금 {s}", en: "Change key and capo, now {s}", ja: "キーとカポを変更、現在 {s}" },
  "key.title": { ko: "키 · 카포", en: "Key · Capo", ja: "キー · カポ" },
  "key.transpose": { ko: "조옮김", en: "Transpose", ja: "移調" },
  "key.capo": { ko: "카포", en: "Capo", ja: "カポ" },
  "key.original": { ko: "원곡", en: "Original", ja: "原曲" },
  "key.original_key": { ko: "원곡 키", en: "Original key", ja: "原曲キー" },
  "key.key_of": { ko: "{k} 키", en: "key of {k}", ja: "{k}キー" },
  "key.orig_of": { ko: "원곡 {k}", en: "orig. {k}", ja: "原曲 {k}" },
  "key.down": { ko: "반음 내리기", en: "Down a semitone", ja: "半音下げる" },
  "key.up": { ko: "반음 올리기", en: "Up a semitone", ja: "半音上げる" },
  "key.capo_down": { ko: "카포 한 프렛 내리기", en: "Capo down a fret", ja: "カポを1フレット下げる" },
  "key.capo_up": { ko: "카포 한 프렛 올리기", en: "Capo up a fret", ja: "カポを1フレット上げる" },
  "key.capo_none": { ko: "없음", en: "None", ja: "なし" },
  "key.capo_fret": { ko: "{n}프렛", en: "Fret {n}", ja: "{n}フレット" },
  "key.capo_tag": { ko: "카포 {n}", en: "Capo {n}", ja: "カポ {n}" },
  "key.capo_shapes": { ko: "코드는 카포 {n} 기준 모양", en: "chords are capo {n} shapes", ja: "コードはカポ{n}の形" },
  "key.barre": { ko: "바레 코드 {n}개", en: "{n} barre chords", ja: "セーハ {n}個" },
  "key.suggest": { ko: "추천: 카포 {c}", en: "Suggested: capo {c}", ja: "おすすめ: カポ {c}" },
  "key.suggest_detail": { ko: "바레 코드 {a}개 → {b}개", en: "barre chords {a} → {b}", ja: "セーハ {a}個 → {b}個" },
  "key.apply": { ko: "적용", en: "Apply", ja: "適用" },
  "key.applied": { ko: "적용됨", en: "Applied", ja: "適用中" },
  "key.suggest_none": { ko: "이 키에서는 카포 없이 잡을 때 바레 코드가 가장 적어요.", en: "In this key, playing without a capo already has the fewest barre chords.", ja: "このキーではカポなしがいちばんセーハが少なくなります。" },
  "key.reset": { ko: "원래대로", en: "Reset", ja: "元に戻す" },
  "key.copy_link": { ko: "링크 복사", en: "Copy link", ja: "リンクをコピー" },
  "key.link_copied": { ko: "링크를 복사했어요. 받은 사람도 같은 키·카포로 열려요.", en: "Link copied. It opens with the same key and capo.", ja: "リンクをコピーしました。同じキー・カポで開きます。" },
  "key.copy_failed": { ko: "복사하지 못했어요. 주소창의 링크를 복사해 주세요.", en: "Couldn't copy. Copy the link from the address bar.", ja: "コピーできませんでした。アドレスバーのリンクをコピーしてください。" },
  "key.note": { ko: "원곡 소리는 그대로예요. 이 기기에서 이 곡을 다시 열면 지금 설정으로 열립니다.", en: "The recording keeps its pitch. Reopening this song on this device uses these settings.", ja: "原曲の音はそのままです。この端末でこの曲を開き直すと、今の設定で開きます。" },
  "key.restored": { ko: "지난번 설정으로 열었어요 · {s}", en: "Opened with your last settings · {s}", ja: "前回の設定で開きました · {s}" },
  "key.orig_tag": { ko: "원래 키", en: "Original key", ja: "原曲キー" },
  "key.pdf_note": { ko: "PDF 악보는 올린 파일 그대로 원래 키로 보이고, 재생 바도 원래 키 코드를 보여 줘요. 코드표에서는 {s}로 보입니다.", en: "Your PDF stays in the original key, and so do the chords in the play bar. The chord chart shows {s}.", ja: "PDF楽譜はアップロードしたまま原曲キーで表示され、再生バーのコードも原曲キーです。コード譜では{s}で表示されます。" },

  // export (chord chart only — the user's PDF is theirs)
  "export.btn": { ko: "내보내기", en: "Export", ja: "書き出し" },
  "export.note": { ko: "{s} 기준으로 내보냅니다", en: "Exports in {s}", ja: "{s}で書き出します" },
  "export.pdf": { ko: "PDF 다운로드", en: "Download PDF", ja: "PDFをダウンロード" },
  "export.pdf_sub": { ko: "A4 파일로 바로 저장돼요", en: "Saves an A4 file right away", ja: "A4のファイルをすぐ保存します" },
  "export.pdf_busy": { ko: "PDF 만드는 중…", en: "Making the PDF…", ja: "PDFを作成中…" },
  "export.pdf_done": { ko: "PDF를 저장했어요.", en: "PDF saved.", ja: "PDFを保存しました。" },
  "export.pdf_fail": { ko: "PDF를 만들지 못했어요. ‘인쇄’에서 PDF로 저장해 주세요.", en: "Couldn't make the PDF. Use Print and save as PDF instead.", ja: "PDFを作成できませんでした。「印刷」からPDFに保存してください。" },
  "export.print": { ko: "인쇄", en: "Print", ja: "印刷" },
  "export.print_sub": { ko: "브라우저 인쇄 창을 열어요", en: "Opens the print dialog", ja: "印刷ダイアログを開きます" },
  "export.file_suffix": { ko: "코드 악보", en: "chord chart", ja: "コード譜" },
  "export.diagrams": { ko: "운지 그림 포함", en: "Include chord diagrams", ja: "コード図を入れる" },
  "export.text": { ko: "텍스트 복사", en: "Copy as text", ja: "テキストをコピー" },
  "export.text_sub": { ko: "누르면 바로 복사돼요", en: "Copies right away", ja: "すぐにコピーされます" },
  "export.copied": { ko: "복사했어요. 카톡이나 메모에 붙여넣으세요.", en: "Copied. Paste it into a chat or a note.", ja: "コピーしました。チャットやメモに貼り付けてください。" },
  "export.view": { ko: "내용 보기", en: "View", ja: "内容を見る" },
  "export.copy_blocked": { ko: "자동 복사가 막혀 있어 텍스트를 선택해 두었어요.", en: "Copying was blocked, so the text is selected for you.", ja: "自動コピーできなかったため、テキストを選択しました。" },
  "export.preview_title": { ko: "복사한 내용", en: "Copied text", ja: "コピーした内容" },
  "export.copy_again": { ko: "다시 복사", en: "Copy again", ja: "もう一度コピー" },
  "export.inapp_title_kakao": { ko: "카카오톡 안에서는 인쇄·PDF 저장이 안 돼요", en: "Printing and PDF downloads don't work inside KakaoTalk", ja: "カカオトーク内では印刷・PDF保存ができません" },
  "export.inapp_title": { ko: "앱 안에서 연 화면에서는 인쇄·PDF 저장이 안 돼요", en: "Printing and PDF downloads don't work inside this app", ja: "アプリ内のブラウザでは印刷・PDF保存ができません" },
  "export.inapp_body": { ko: "Safari나 Chrome에서 열면 인쇄하거나 PDF로 저장할 수 있어요. 지금 보는 {s} 설정도 링크에 담겨 그대로 열립니다.", en: "Open it in Safari or Chrome to print or save a PDF. The link keeps your {s} setting.", ja: "SafariやChromeで開くと印刷・PDF保存ができます。今の{s}の設定もリンクに含まれます。" },
  "export.inapp_menu": { ko: "화면의 더보기 메뉴에서 ‘다른 브라우저로 열기’를 눌러 주세요.", en: "Use the app's menu and choose “Open in browser”.", ja: "画面のメニューから「ブラウザで開く」を選んでください。" },
  "export.inapp_text": { ko: "카톡이나 메모에 붙여넣을 거라면 텍스트 복사로 충분해요.", en: "To paste into a chat or a note, copying as text is enough.", ja: "チャットやメモに貼るなら、テキストのコピーで十分です。" },
  "export.inapp_open": { ko: "외부 브라우저로 열기", en: "Open in browser", ja: "外部ブラウザで開く" },
  "export.footer": { ko: "Tutti 코드 악보", en: "Tutti chord chart", ja: "Tutti コード譜" },

  // controls (titles / labels)
  "ctrl.play": { ko: "재생 / 일시정지", en: "Play / Pause", ja: "再生 / 一時停止" },
  "ctrl.settings": { ko: "설정", en: "Settings", ja: "設定" },
  "ctrl.volume": { ko: "볼륨", en: "Volume", ja: "音量" },
  "ctrl.cursorcomp": { ko: "커서 보정", en: "Cursor offset", ja: "カーソル補正" },
  "ctrl.language": { ko: "언어", en: "Language", ja: "言語" },

  // footer
  "footer.privacy": { ko: "개인정보처리방침", en: "Privacy Policy", ja: "プライバシーポリシー" },
  "footer.popular": { ko: "인기 악보", en: "Popular", ja: "人気の楽譜" },
  "footer.about": { ko: "소개", en: "About", ja: "紹介" },
  "footer.contact": { ko: "문의", en: "Contact", ja: "お問い合わせ" },
  "footer.terms": { ko: "이용약관", en: "Terms", ja: "利用規約" },

  // account / saved scores

  // about (home SEO/intro)
  "about.h2": { ko: "Tutti — YouTube·오디오 코드 분석", en: "Tutti — YouTube & audio chord analysis", ja: "Tutti — YouTube・音声コード解析" },
  "about.intro": {
    ko: "Tutti는 YouTube 링크나 오디오·영상 파일을 분석해 곡의 키, 템포(BPM), 박자, 코드 진행을 자동으로 추출하고 기타 코드 악보와 타브(TAB) 악보를 만들어 주는 무료 도구입니다. 분석 결과는 재생 커서와 함께 표시돼 따라 연습하기 좋습니다.",
    en: "Tutti is a free tool that analyzes a YouTube link or an audio/video file to detect the key, tempo (BPM), time signature and chord progression, then builds guitar chord charts and TAB. Results are shown with a playback cursor so you can play along.",
    ja: "Tuttiは、YouTubeリンクや音声・動画ファイルを解析してキー・テンポ(BPM)・拍子・コード進行を自動抽出し、ギターのコード譜とTAB譜を作る無料ツールです。結果は再生カーソルと一緒に表示され、合わせて演奏できます。",
  },
  "about.how_h": { ko: "이렇게 사용하세요", en: "How to use", ja: "使い方" },
  "about.how1": { ko: "오디오·영상 파일을 올리거나 YouTube 링크를 붙여넣고 분석을 누릅니다. (10분 이하)", en: "Upload an audio/video file or paste a YouTube link and hit Analyze. (≤10 min)", ja: "音声・動画ファイルをアップロードするかYouTubeリンクを貼って解析を押します。（10分以下）" },
  "about.how2": { ko: "키·BPM과 함께 코드 악보가 자동으로 그려집니다. 악보/코드 보기를 전환할 수 있어요.", en: "The chord chart is drawn automatically with key & BPM. Switch between chord and TAB views.", ja: "キー・BPMとともにコード譜が自動で描かれます。コード／TAB表示を切り替えられます。" },
  "about.how3": { ko: "재생하면 커서가 현재 위치를 따라가고, 편집 모드에서 음표·주법·코드를 직접 고칠 수 있습니다.", en: "Play and the cursor follows along; in edit mode you can change notes, techniques and chords.", ja: "再生するとカーソルが現在位置を追い、編集モードで音符・奏法・コードを直接編集できます。" },
  "about.feat_h": { ko: "이런 걸 할 수 있어요", en: "What you can do", ja: "できること" },
  "about.feat1": { ko: "코드 악보 만들기 — 마디별 코드 진행을 한눈에", en: "Make chord charts — see the progression by measure", ja: "コード譜を作成 — 小節ごとの進行が一目で" },
  "about.feat2": { ko: "타브(TAB) 악보 — 기타 운지·주법까지 표기", en: "TAB notation — frets and techniques", ja: "TAB譜 — 運指・奏法まで表記" },
  "about.feat3": { ko: "키·BPM·박자 분석 — 곡의 기본 정보를 자동 추출", en: "Key/BPM/meter analysis — automatic song info", ja: "キー・BPM・拍子の解析 — 曲の基本情報を自動抽出" },
  "about.feat4": { ko: "재생 커서 연습 — 노래/악보에 맞춰 따라 연주", en: "Play-along cursor — follow the song or score", ja: "再生カーソル練習 — 曲・譜面に合わせて演奏" },
  "about.faq_h": { ko: "자주 묻는 질문", en: "FAQ", ja: "よくある質問" },
  "about.faq1": { ko: "<b>어떤 곡이든 되나요?</b> 10분 이하의 오디오·영상이면 분석할 수 있습니다. 코드는 오디오 분석 기반 추정이라 곡에 따라 정확도가 다를 수 있어요.", en: "<b>Any song?</b> Any audio/video under 10 minutes. Chords are estimated from audio analysis, so accuracy varies by song.", ja: "<b>どんな曲でも？</b> 10分以下の音声・動画なら解析できます。コードは音声解析による推定のため、曲により精度が異なります。" },
  "about.faq2": { ko: "<b>무료인가요?</b> 네, 별도 가입 없이 무료로 사용할 수 있습니다.", en: "<b>Is it free?</b> Yes, free to use with no sign-up.", ja: "<b>無料ですか？</b> はい、登録不要で無料で使えます。" },
  "about.faq3": { ko: '<b>내 파일은 저장되나요?</b> 업로드한 원본은 분석 직후 삭제되며 저장하지 않습니다. 자세한 내용은 <a href="/privacy">개인정보처리방침</a>을 참고하세요.', en: '<b>Are my files stored?</b> Uploaded originals are deleted right after analysis and never stored. See the <a href="/privacy">Privacy Policy</a>.', ja: '<b>ファイルは保存されますか？</b> アップロードした原本は解析直後に削除され、保存しません。詳しくは<a href="/privacy">プライバシーポリシー</a>をご覧ください。' },

  // editor dock
  "fb.tip": { ko: "{s}번줄 {f}프렛 · {note}", en: "String {s}, fret {f} · {note}", ja: "{s}弦 {f}フレット · {note}" },
  "fb.dead": { ko: "{s}번줄 뮤트(✕)", en: "String {s} mute (✕)", ja: "{s}弦ミュート(✕)" },

  // note/rest duration names (tooltips + inspector)
  "dur.n.1": { ko: "온음표", en: "Whole note", ja: "全音符" }, "dur.n.2": { ko: "2분음표", en: "Half note", ja: "2分音符" },
  "dur.n.4": { ko: "4분음표", en: "Quarter note", ja: "4分音符" }, "dur.n.8": { ko: "8분음표", en: "Eighth note", ja: "8分音符" },
  "dur.n.16": { ko: "16분음표", en: "16th note", ja: "16分音符" }, "dur.n.32": { ko: "32분음표", en: "32nd note", ja: "32分音符" },
  "dur.r.1": { ko: "온쉼표", en: "Whole rest", ja: "全休符" }, "dur.r.2": { ko: "2분쉼표", en: "Half rest", ja: "2分休符" },
  "dur.r.4": { ko: "4분쉼표", en: "Quarter rest", ja: "4分休符" }, "dur.r.8": { ko: "8분쉼표", en: "Eighth rest", ja: "8分休符" },
  "dur.r.16": { ko: "16분쉼표", en: "16th rest", ja: "16分休符" }, "dur.r.32": { ko: "32분쉼표", en: "32nd rest", ja: "32分休符" },

  // technique groups + labels
  "fxgrp.artic": { ko: "이음·꾸밈", en: "Articulation", ja: "アーティキュレーション" },
  "fx.tie": { ko: "이음줄", en: "Tie", ja: "タイ" },
  "fx.grh": { ko: "꾸밈해머", en: "Grace (H)", ja: "装飾(H)" },
  "fx.grsl": { ko: "꾸밈슬라", en: "Grace (sl)", ja: "装飾(sl)" },
  "fxgrp.bend": { ko: "벤드", en: "Bend", ja: "ベンド" },
  "fxgrp.slide": { ko: "슬라이드", en: "Slide", ja: "スライド" },
  "fxgrp.tech": { ko: "주법", en: "Technique", ja: "奏法" },
  "fxgrp.nuance": { ko: "뉘앙스", en: "Nuance", ja: "ニュアンス" },
  "fxgrp.harmonics": { ko: "하모닉스", en: "Harmonics", ja: "ハーモニクス" },
  "fx.b": { ko: "풀벤드", en: "Full bend", ja: "フルベンド" },
  "fx.bh": { ko: "하프벤드", en: "Half bend", ja: "ハーフベンド" },
  "fx.bq": { ko: "쿼터벤드", en: "1/4 bend", ja: "クォーターベンド" },
  "fx.br": { ko: "벤드릴리즈", en: "Bend/release", ja: "ベンド/リリース" },
  "fx.h": { ko: "H/P", en: "H/P", ja: "H/P" },
  "fx.sl": { ko: "슬라", en: "Slide", ja: "スライド" },
  "fx.ss": { ko: "시프트슬라", en: "Shift slide", ja: "シフトスライド" },
  "fx.sib": { ko: "슬인↗", en: "Slide in↗", ja: "スライドイン↗" },
  "fx.sia": { ko: "슬인↘", en: "Slide in↘", ja: "スライドイン↘" },
  "fx.sou": { ko: "슬아웃↗", en: "Slide out↗", ja: "スライドアウト↗" },
  "fx.sod": { ko: "슬아웃↘", en: "Slide out↘", ja: "スライドアウト↘" },
  "fx.v": { ko: "비브", en: "Vibrato", ja: "ビブラート" },
  "fx.vw": { ko: "와이드비브", en: "Wide vib", ja: "ワイドビブ" },
  "fx.tr": { ko: "트릴", en: "Trill", ja: "トリル" },
  "fx.tt": { ko: "태핑", en: "Tapping", ja: "タッピング" },
  "fx.tp": { ko: "트레몰로", en: "Tremolo", ja: "トレモロ" },
  "fx.pm": { ko: "팜뮤트", en: "Palm mute", ja: "パームミュート" },
  "fx.lr": { ko: "레링", en: "Let ring", ja: "レットリング" },
  "fx.st": { ko: "스타카토", en: "Staccato", ja: "スタッカート" },
  "fx.ac": { ko: "악센트", en: "Accent", ja: "アクセント" },
  "fx.x": { ko: "데드음", en: "Dead note", ja: "デッドノート" },
  "fx.nh": { ko: "내추럴HM", en: "Nat. harm", ja: "ナチュラルHM" },
  "fx.ah": { ko: "인공HM", en: "Art. harm", ja: "人工HM" },
  "fx.ph": { ko: "피킹HM", en: "Pinch harm", ja: "ピッキングHM" },
};

function detect(): Lang {
  try {
    const u = new URLSearchParams(location.search).get("lang");
    if (u === "ko" || u === "en" || u === "ja") return u;
    const s = localStorage.getItem("tutti.lang");
    if (s === "ko" || s === "en" || s === "ja") return s;
    const n = (navigator.language || "").slice(0, 2);
    return n === "ko" ? "ko" : n === "ja" ? "ja" : "en";
  } catch {
    return "ko";
  }
}

interface LangState { lang: Lang; setLang: (l: Lang) => void }
export const useLang = create<LangState>((set) => ({
  lang: detect(),
  setLang: (lang) => {
    try { localStorage.setItem("tutti.lang", lang); } catch { /* ignore */ }
    try { document.documentElement.lang = lang; } catch { /* ignore */ }
    set({ lang });
  },
}));

// set <html lang> on first load
try { document.documentElement.lang = useLang.getState().lang; } catch { /* ignore */ }

// hook: returns a translator that re-renders when the language changes.
// supports {name} interpolation: t("pdf.counts", { s: 32, a: 84 })
export function useT() {
  const lang = useLang((s) => s.lang);
  return (key: string, params?: Record<string, string | number>) => {
    let s = DICT[key]?.[lang] ?? DICT[key]?.ko ?? key;
    if (params) for (const k in params) s = s.split(`{${k}}`).join(String(params[k]));
    return s;
  };
}
